import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, CircularProgress, IconButton, InputAdornment, MenuItem, Stack,
  Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon, Search as SearchIcon, EditOutlined as EditIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import PromoChip from 'components/shared/PromoChip';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import CouponForm from 'components/admin-coupons/CouponForm';
import { COUPONS, DELETE_COUPONS } from 'api/graphql/coupon';

const PER_PAGE = 15;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };

function CouponList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['coupons', 'buttons']);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [form, setForm] = useState({ open: false, coupon: null });
  const [del, setDel] = useState({ open: false, id: null });

  const { data, loading, refetch } = useQuery(COUPONS, {
    variables: { first: PER_PAGE, page: page + 1, search, status: status || null },
    fetchPolicy: 'cache-and-network'
  });
  const [deleteCoupons] = useMutation(DELETE_COUPONS);

  const rows = data?.coupons?.data ?? [];
  const total = data?.coupons?.paginatorInfo?.total ?? 0;

  const statusFilters = [
    { value: '', label: t('allStatuses', { ns: 'coupons', defaultValue: 'Todos' }) },
    { value: 'active', label: t('statusActive', { ns: 'coupons', defaultValue: 'Activos' }) },
    { value: 'inactive', label: t('statusInactive', { ns: 'coupons', defaultValue: 'Inactivos' }) },
    { value: 'expired', label: t('statusExpired', { ns: 'coupons', defaultValue: 'Expirados' }) },
    { value: 'scheduled', label: t('statusScheduled', { ns: 'coupons', defaultValue: 'Programados' }) }
  ];

  const confirmDelete = async () => {
    if (del.id) await deleteCoupons({ variables: { id: [del.id] } });
    setDel({ open: false, id: null });
    refetch();
  };

  const usesLabel = (c) => `${c.usage_count}${c.max_uses ? ` / ${c.max_uses}` : ''}`;
  const audienceLabel = (c) => {
    if (c.audience === 'national') return t('audienceNational', { ns: 'coupons', defaultValue: 'Nacionales' });
    if (c.audience === 'international') return t('audienceInternational', { ns: 'coupons', defaultValue: 'Internacionales' });
    return t('audienceAll', { ns: 'coupons', defaultValue: 'Todos' });
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          placeholder={t('searchCode', { ns: 'coupons', defaultValue: 'Buscar código' })}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          fullWidth
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
        />
        <TextField select size="small" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} sx={{ minWidth: 180 }}>
          {statusFilters.map((s) => <MenuItem key={s.value || 'all'} value={s.value}>{s.label}</MenuItem>)}
        </TextField>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setForm({ open: true, coupon: null })} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {t('newCoupon', { ns: 'coupons', defaultValue: 'Nuevo cupón' })}
        </Button>
      </Stack>

      <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.content' }}>
        {loading && rows.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: 'gold.main' }} /></Box>
        )}
        {!loading && rows.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>{t('empty', { ns: 'coupons', defaultValue: 'No hay cupones.' })}</Box>
        )}

        {rows.length > 0 && (isMobile ? (
          <Box>
            {rows.map((c) => (
              <Stack key={c.id} direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1.25, borderBottom: (th) => `2px solid ${th.palette.stackedRowBorder}`, '&:last-of-type': { borderBottom: 0 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>{c.code}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{fmtDate(c.valid_from)} – {fmtDate(c.valid_to)} · {usesLabel(c)}</Typography>
                </Box>
                <PromoChip discountValue={c.discount_value} discountType={c.discount_type} />
                <IconButton size="small" onClick={() => setForm({ open: true, coupon: c })}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => setDel({ open: true, id: c.id })} sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('code', { ns: 'coupons', defaultValue: 'Código' })}</TableCell>
                  <TableCell>{t('discount', { ns: 'coupons', defaultValue: 'Descuento' })}</TableCell>
                  <TableCell>{t('audience', { ns: 'coupons', defaultValue: 'Aplica a' })}</TableCell>
                  <TableCell>{t('validity', { ns: 'coupons', defaultValue: 'Vigencia' })}</TableCell>
                  <TableCell align="center">{t('uses', { ns: 'coupons', defaultValue: 'Usos' })}</TableCell>
                  <TableCell>{t('plans', { ns: 'coupons', defaultValue: 'Planes' })}</TableCell>
                  <TableCell align="center">{t('status', { ns: 'coupons', defaultValue: 'Estado' })}</TableCell>
                  <TableCell align="right">{t('actions', { ns: 'coupons', defaultValue: 'Acciones' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell sx={{ fontWeight: 800 }}>{c.code}</TableCell>
                    <TableCell><PromoChip discountValue={c.discount_value} discountType={c.discount_type} /></TableCell>
                    <TableCell>
                      <Chip size="small" label={audienceLabel(c)} sx={{ backgroundColor: 'surface.raised', color: 'text.secondary', fontWeight: 600 }} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(c.valid_from)} – {fmtDate(c.valid_to)}</TableCell>
                    <TableCell align="center">{usesLabel(c)}</TableCell>
                    <TableCell>{c.plans?.length ? c.plans.map((p) => p.name).join(', ') : t('appliesAll', { ns: 'coupons', defaultValue: 'Todos' })}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={c.is_active ? t('on', { ns: 'coupons', defaultValue: 'Activo' }) : t('off', { ns: 'coupons', defaultValue: 'Inactivo' })}
                        sx={{ backgroundColor: c.is_active ? 'rgba(27,147,75,0.16)' : 'rgba(128,128,128,0.18)', color: c.is_active ? '#5FE0A0' : 'text.secondary', fontWeight: 700 }} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setForm({ open: true, coupon: c })}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setDel({ open: true, id: c.id })} sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ))}

        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={PER_PAGE} rowsPerPageOptions={[PER_PAGE]} />
      </Box>

      {form.open && <CouponForm open={form.open} coupon={form.coupon} onClose={() => { setForm({ open: false, coupon: null }); refetch(); }} />}
      <ConfirmDeleteModal open={del.open} onClose={() => setDel({ open: false, id: null })} onConfirm={confirmDelete} count={1} />
    </Box>
  );
}

export default CouponList;
